import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReaderById, getReaderStats, getReaderReadingHistory, updateReader, deleteReader } from '../services/readerService';

function ReaderProfile() {
  const { readerId } = useParams();
  const navigate = useNavigate();
  
  const [reader, setReader] = useState(null);
  const [stats, setStats] = useState(null);
  const [readingHistory, setReadingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Load reader data on component mount
  useEffect(() => {
    loadReaderData();
  }, [readerId]);
  
  const loadReaderData = async () => {
    if (!readerId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch reader details
      const readerResponse = await getReaderById(readerId);
      setReader(readerResponse.reader);
      setEditName(readerResponse.reader.name);
      
      // Fetch reader stats
      const statsResponse = await getReaderStats(readerId);
      setStats(statsResponse.stats);
      
      // Fetch reading history
      const historyResponse = await getReaderReadingHistory(readerId);
      setReadingHistory(historyResponse.chaptersRead || []);
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to load reader data:', err);
      setError('Failed to load reader data. Please try again.');
      setLoading(false);
    }
  };
  
  const handleUpdateReader = async (e) => {
    e.preventDefault();
    
    if (!editName.trim()) {
      setError('Reader name cannot be empty');
      return;
    }
    
    try {
      setLoading(true);
      await updateReader(readerId, { name: editName });
      
      // Reload reader data
      await loadReaderData();
      
      setIsEditing(false);
      setSuccess('Reader updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to update reader:', err);
      setError('Failed to update reader. Please try again.');
      setLoading(false);
    }
  };
  
  const handleDeleteReader = async () => {
    try {
      setLoading(true);
      await deleteReader(readerId);
      
      // Navigate back to family page
      navigate('/family');
    } catch (err) {
      console.error('Failed to delete reader:', err);
      setError('Failed to delete reader. Please try again.');
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };
  
  if (loading && !reader) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }
  
  if (error && !reader) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/family')}
        >
          Back to Family
        </button>
      </div>
    );
  }
  
  if (!reader) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          Reader not found
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/family')}
        >
          Back to Family
        </button>
      </div>
    );
  }
  
  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Reader Profile</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => navigate('/family')}
        >
          Back to Family
        </button>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}
      
      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h2 className="mb-0">Reader Information</h2>
          {!isEditing ? (
            <button 
              className="btn btn-sm btn-outline-primary" 
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          ) : (
            <button 
              className="btn btn-sm btn-outline-secondary" 
              onClick={() => {
                setIsEditing(false);
                setEditName(reader.name);
              }}
            >
              Cancel
            </button>
          )}
        </div>
        <div className="card-body">
          {!isEditing ? (
            <div>
              <h3>{reader.name}</h3>
              <p>Family: {reader.family_name}</p>
              <p>Created: {new Date(reader.created_at).toLocaleDateString()}</p>
            </div>
          ) : (
            <form onSubmit={handleUpdateReader}>
              <div className="mb-3">
                <label htmlFor="reader-name" className="form-label">Reader Name</label>
                <input
                  type="text"
                  className="form-control"
                  id="reader-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                />
              </div>
              <div className="d-flex justify-content-between">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete Reader
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      
      <div className="card mb-4">
        <div className="card-header">
          <h2 className="mb-0">Reading Statistics</h2>
        </div>
        <div className="card-body">
          {stats ? (
            <div className="row">
              <div className="col-md-4">
                <div className="card text-center mb-3">
                  <div className="card-body">
                    <h5 className="card-title">Total Chapters</h5>
                    <p className="display-4">{stats.totalChaptersRead || 0}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-center mb-3">
                  <div className="card-body">
                    <h5 className="card-title">Old Testament</h5>
                    <p className="display-4">{stats.oldTestamentCount || 0}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card text-center mb-3">
                  <div className="card-body">
                    <h5 className="card-title">New Testament</h5>
                    <p className="display-4">{stats.newTestamentCount || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p>No statistics available</p>
          )}
        </div>
      </div>
      
      <div className="card">
        <div className="card-header">
          <h2 className="mb-0">Reading History</h2>
        </div>
        <div className="card-body">
          {readingHistory.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Book</th>
                    <th>Chapter</th>
                    <th>Date Read</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {readingHistory.map(entry => (
                    <tr key={entry.id}>
                      <td>{entry.book}</td>
                      <td>{entry.chapter}</td>
                      <td>{new Date(entry.read_date).toLocaleDateString()}</td>
                      <td>{entry.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No reading history available</p>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-wrapper">
          <div className="modal show d-block" tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowDeleteConfirm(false)} 
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete the reader "{reader.name}"?</p>
                  <p className="text-danger">This action cannot be undone and will delete all reading history for this reader.</p>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger" 
                    onClick={handleDeleteReader}
                    disabled={loading}
                  >
                    {loading ? 'Deleting...' : 'Delete Reader'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowDeleteConfirm(false)}></div>
        </div>
      )}
    </div>
  );
}

export default ReaderProfile; 